import { useState } from 'react';

/**
 * The credit ladder, as one rail you drag.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT A GRID OF CARDS
 *
 * It was, and the ladder going from three rungs to eight made it obviously
 * wrong: eight boxes down a phone screen, each asking the same unanswerable
 * question — is this a 25,000-credit month or a 500,000 one? Nobody knows before
 * they have run out, and the scroll makes the largest option feel like the
 * default simply by being last.
 *
 * A rail asks the question people can answer: how much do you want to add. One
 * control, one number moving, one price moving with it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LINE UNDER IT IS THE POINT
 *
 * "These 25,000 are ADDED to the 20,000 your plan includes: 45,000 this month."
 *
 * Buying credits beside a plan that also grants credits reads to a careful
 * person as a possible swap, and being wrong about that costs them money. The
 * total is computed and shown rather than implied.
 */

export type CreditPack = {
  code: string;
  credits: number;
  price_usd: number;
  price_bhd: number;
  percent_off: number;
  popular?: boolean;
  approx_exchanges?: number;
};

export function CreditSlider({
  packs,
  includedCredits,
  locale,
  disabled,
  busy,
  onBuy,
  labels,
}: {
  packs: CreditPack[];
  /** What the plan already grants each month, so the total is the truth. */
  includedCredits: number;
  locale: string;
  disabled?: boolean;
  busy?: boolean;
  onBuy: (pack: CreditPack) => void;
  labels: {
    credits: string;
    addition: string;
    saving: string;
    cta: string;
    busy: string;
  };
}) {
  /* Starts on the second rung, not the first. The cheapest option as the
     default makes the control look like a decision already taken; one step in,
     the rail reads as a range with room in both directions. */
  const [index, setIndex] = useState(() => Math.min(1, Math.max(0, packs.length - 1)));

  if (packs.length === 0) return null;
  const pack = packs[Math.min(index, packs.length - 1)]!;
  const n = (v: number) => v.toLocaleString(locale);
  const short = (v: number) => (v >= 1_000_000 ? `${v / 1_000_000}M` : `${v / 1000}k`);

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-[30px] font-semibold leading-none tracking-tight tabular-nums">
          {n(pack.credits)}
        </span>
        <span className="text-[13px] text-muted-foreground">{labels.credits}</span>
        <span className="ms-auto text-[20px] font-semibold tabular-nums">
          USD {pack.price_usd}
        </span>
      </div>

      <input
        type="range"
        min={0}
        max={packs.length - 1}
        step={1}
        value={index}
        disabled={disabled}
        onChange={(e) => setIndex(Number(e.target.value))}
        aria-label={labels.credits}
        /* `accent-foreground` keeps the control monochrome: this brand has no
           hue to spend on a slider. */
        className="mt-5 w-full accent-foreground disabled:opacity-50"
      />

      {/* The rungs, clickable. Dragging is imprecise on a phone and the labels
          are the only way to land on a specific one deliberately. */}
      <div className="mt-2 flex justify-between text-[10.5px] text-muted-foreground">
        {packs.map((p, i) => (
          <button
            key={p.code}
            type="button"
            disabled={disabled}
            onClick={() => setIndex(i)}
            className={
              i === index
                ? 'font-semibold text-foreground'
                : 'transition-colors hover:text-foreground'
            }
          >
            {short(p.credits)}
          </button>
        ))}
      </div>

      <div className="mt-5 rounded-xl border bg-muted/40 p-4">
        <p className="text-[13px] leading-relaxed">
          {labels.addition
            .replace('{pack}', n(pack.credits))
            .replace('{included}', n(includedCredits))
            .replace('{total}', n(includedCredits + pack.credits))}
        </p>
        {pack.percent_off > 0 ? (
          <p className="mt-1.5 text-[12px] text-muted-foreground">
            {labels.saving.replace('{percent}', String(pack.percent_off))}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => onBuy(pack)}
        className="mt-4 h-10 w-full rounded-md bg-foreground text-[14px] font-semibold text-background transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {busy ? labels.busy : labels.cta.replace('{price}', `USD ${pack.price_usd}`)}
      </button>
    </div>
  );
}
